import { BETA_FEATURE_PAGE_PREFIX, isBetaFeaturePageSlug, pageSlugToRoute } from "@/features/auth/lib/page-routes";

type PagePermissionSource = {
  id: string;
  permissions: Array<{ type: string; route: string }>;
};

function collectPageSlugs(tenants: PagePermissionSource[]): string[] {
  const seen = new Set<string>();
  const slugs: string[] = [];
  for (const tenant of tenants) {
    for (const perm of tenant.permissions) {
      if (perm.type !== "PAGE") continue;
      if (seen.has(perm.route) || !pageSlugToRoute(perm.route)) continue;
      seen.add(perm.route);
      slugs.push(perm.route);
    }
  }
  return slugs;
}

export function partitionPageSlugs(slugs: string[]): {
  regular: string[];
  beta: string[];
} {
  const regular: string[] = [];
  const beta: string[] = [];
  const seen = new Set<string>();
  for (const slug of slugs) {
    if (seen.has(slug)) continue;
    seen.add(slug);
    if (isBetaFeaturePageSlug(slug)) {
      beta.push(slug);
    } else {
      regular.push(slug);
    }
  }
  return { regular, beta };
}

export function betaFeatureChildSlug(pageSlug: string): string {
  return pageSlug.slice(BETA_FEATURE_PAGE_PREFIX.length);
}

export function betaFeatureNavLabel(pageSlug: string): string {
  return betaFeatureChildSlug(pageSlug)
    .split("-")
    .filter((word) => word.length > 0)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Regular pages stay unioned across tenants (existing Sidebar behavior).
 * Beta children follow the active tenant only — same rule as TopNav.
 */
function partitionCollectedSlugs(sources: PagePermissionSource[]): {
  regular: string[];
  beta: string[];
} {
  return partitionPageSlugs(collectPageSlugs(sources));
}

export function resolveSidebarPageSlugs(
  tenants: PagePermissionSource[] | undefined,
  activeTenantId: string | null,
): { regular: string[]; beta: string[] } {
  const all = tenants ?? [];
  const active = all.find((tenant) => tenant.id === activeTenantId) ?? all[0];
  return {
    regular: partitionCollectedSlugs(all).regular,
    beta: partitionCollectedSlugs(active ? [active] : []).beta,
  };
}
